import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  TextField,
  MenuItem,
  Grid,
  Button,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  Pagination
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Refresh as RefreshIcon,
  FilterList as FilterListIcon,
  Build as BuildIcon,
  Visibility as VisibilityIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Delete as DeleteIcon,
  Warning as WarningIcon,
  Lock as LockIcon
} from '@mui/icons-material';
import { format } from 'date-fns';
import { ar } from 'date-fns/locale';
import { apiService } from '../services/apiService';
import { usePermissions } from '../hooks/usePermissions';

interface InventoryEvent {
  _id: string;
  eventType: string;
  eventTypeAr: string;
  requestId: string | {
    _id: string;
    fromWarehouse?: { name: string };
    toWarehouse?: { name: string };
    status?: string;
    date?: string;
  };
  transferType?: string;
  referenceNumber?: string;
  status: string;
  step: string;
  stepNameAr: string;
  details: any;
  error?: string;
  userId?: { name: string };
  timestamp: string;
  statusIcon: string;
  requiresFix: boolean;
}

interface GroupedRequest {
  requestId: string;
  transferType: 'request' | 'direct';
  referenceNumber?: string;
  fromWarehouse: string;
  toWarehouse: string;
  events: InventoryEvent[];
  totalSteps: number;
  completedSteps: number;
  failedSteps: number;
  lastUpdate: string;
  status: 'success' | 'failed' | 'partial';
}

interface Statistics {
  total: number;
  success: number;
  failed: number;
  needingFix: number;
}

interface EventsCounts {
  total: number;
  olderThan3Months: number;
  olderThan6Months: number;
}

const InventoryEvents: React.FC = () => {
  // التحقق من الصلاحيات
  const { hasPermission } = usePermissions();
  const canAccessPage = hasPermission('warehouses', 'inventory-events', 'view_page');
  const canViewDetails = hasPermission('warehouses', 'inventory-events', 'view');
  const canDeleteOld = hasPermission('warehouses', 'inventory-events', 'delete_old');

  const [events, setEvents] = useState<InventoryEvent[]>([]);
  const [groupedRequests, setGroupedRequests] = useState<GroupedRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statistics, setStatistics] = useState<Statistics | null>(null);
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [eventsCounts, setEventsCounts] = useState<EventsCounts | null>(null);
  
  // Filters
  const [filters, setFilters] = useState({
    status: '',
    eventType: '',
    startDate: '',
    endDate: '',
    needsFixOnly: false,
    search: ''
  });
  
  // Search input state (separate from filters for debouncing)
  const [searchInput, setSearchInput] = useState('');
  
  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setFilters(prev => ({ ...prev, search: searchInput }));
      setPage(1);
    }, 500); // Wait 500ms after user stops typing
    
    return () => clearTimeout(timer);
  }, [searchInput]);
  
  // Pagination
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalEvents, setTotalEvents] = useState(0);
  
  // Dialog states
  const [selectedEvent, setSelectedEvent] = useState<InventoryEvent | null>(null);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [fixDialogOpen, setFixDialogOpen] = useState(false);
  const [fixNotes, setFixNotes] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletePeriod, setDeletePeriod] = useState<'all' | '3months' | '6months'>('3months');
  const [deleting, setDeleting] = useState(false);

  // Load events
  useEffect(() => {
    loadEvents();
    loadStatistics();
    loadEventsCounts();
  }, [page, filters]);

  const loadEvents = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const params: any = {
        page,
        limit: 50,
        ...filters
      };
      
      const response: any = await apiService.get('/inventory-events', { params });
      
      if (response?.success) {
        const eventsData = response.events || [];
        setEvents(eventsData);
        setTotalPages(response.pagination?.totalPages || 1);
        setTotalEvents(response.pagination?.totalEvents || 0);
        
        // تجميع الأحداث حسب رقم الطلب
        const grouped = groupEventsByRequest(eventsData);
        setGroupedRequests(grouped);
      } else {
        setEvents([]);
        setGroupedRequests([]);
        setError(response?.message || 'فشل تحميل الأحداث');
      }
    } catch (err: any) {
      console.error('خطأ في تحميل الأحداث:', err);
      setEvents([]);
      setGroupedRequests([]);
      setError(err.response?.data?.message || 'فشل تحميل الأحداث');
    } finally {
      setLoading(false);
    }
  };

  const groupEventsByRequest = (events: InventoryEvent[]): GroupedRequest[] => {
    const grouped: { [key: string]: GroupedRequest } = {};
    
    events.forEach(event => {
      const requestId = typeof event.requestId === 'string' ? event.requestId : event.requestId._id;
      if (!requestId) return;
      
      if (!grouped[requestId]) {
        const fromWarehouse = typeof event.requestId === 'object' ? event.requestId.fromWarehouse?.name : undefined;
        const toWarehouse = typeof event.requestId === 'object' ? event.requestId.toWarehouse?.name : undefined;
        
        grouped[requestId] = {
          requestId,
          transferType: (event.transferType || 'request') as 'request' | 'direct',
          referenceNumber: event.referenceNumber,
          fromWarehouse: fromWarehouse || 'غير محدد',
          toWarehouse: toWarehouse || 'غير محدد',
          events: [],
          totalSteps: 0,
          completedSteps: 0,
          failedSteps: 0,
          lastUpdate: event.timestamp,
          status: 'success'
        };
      }
      
      grouped[requestId].events.push(event);
      grouped[requestId].totalSteps++;
      
      if (event.status === 'success') {
        grouped[requestId].completedSteps++;
      } else {
        grouped[requestId].failedSteps++;
        grouped[requestId].status = 'failed';
      }
      
      // تحديث آخر وقت
      if (new Date(event.timestamp) > new Date(grouped[requestId].lastUpdate)) {
        grouped[requestId].lastUpdate = event.timestamp;
      }
    });
    
    // تحديد الحالة النهائية
    Object.values(grouped).forEach(group => {
      if (group.failedSteps > 0) {
        group.status = 'failed';
      } else if (group.completedSteps < group.totalSteps) {
        group.status = 'partial';
      }
    });
    
    return Object.values(grouped).sort((a, b) =>
      new Date(b.lastUpdate).getTime() - new Date(a.lastUpdate).getTime()
    );
  };

  const toggleRow = (requestId: string) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(requestId)) {
      newExpanded.delete(requestId);
    } else {
      newExpanded.add(requestId);
    }
    setExpandedRows(newExpanded);
  };

  const loadStatistics = async () => {
    try {
      const params: any = {};
      if (filters.startDate) params.startDate = filters.startDate;
      if (filters.endDate) params.endDate = filters.endDate;
      
      const response: any = await apiService.get('/inventory-events/statistics', { params });
      
      if (response?.success) {
        setStatistics(response.statistics);
      } else {
        setStatistics({
          total: 0,
          success: 0,
          failed: 0,
          needingFix: 0
        });
      }
    } catch (err) {
      console.error('خطأ في تحميل الإحصائيات:', err);
      setStatistics({
        total: 0,
        success: 0,
        failed: 0,
        needingFix: 0
      });
    }
  };

  const loadEventsCounts = async () => {
    try {
      const response: any = await apiService.get('/inventory-events/counts/by-period');
      
      if (response?.success) {
        setEventsCounts(response.counts);
      }
    } catch (err) {
      console.error('خطأ في تحميل عدد السجلات:', err);
    }
  };

  const handleOpenDeleteDialog = () => {
    setDeleteDialogOpen(true);
  };

  const handleDeleteOldEvents = async () => {
    try {
      setDeleting(true);
      
      const response: any = await apiService.post('/inventory-events/cleanup/old-events', {
        period: deletePeriod
      });
      
      if (response?.success) {
        alert(`تم حذف ${response.deletedCount} سجل بنجاح`);
        setDeleteDialogOpen(false);
        loadEvents();
        loadStatistics();
        loadEventsCounts();
      } else {
        alert(response?.message || 'فشل حذف السجلات');
      }
    } catch (err: any) {
      console.error('خطأ في حذف السجلات:', err);
      alert(err.response?.data?.message || 'فشل حذف السجلات');
    } finally {
      setDeleting(false);
    }
  };

  const handleFilterChange = (field: string, value: any) => {
    setFilters(prev => ({ ...prev, [field]: value }));
    setPage(1);
  };

  const handleResetFilters = () => {
    setFilters({
      status: '',
      eventType: '',
      startDate: '',
      endDate: '',
      needsFixOnly: false,
      search: ''
    });
    setSearchInput('');
    setPage(1);
  };

  const handleViewDetails = (event: InventoryEvent) => {
    setSelectedEvent(event);
    setDetailsDialogOpen(true);
  };

  const handleOpenFixDialog = (event: InventoryEvent) => {
    setSelectedEvent(event);
    setFixNotes('');
    setFixDialogOpen(true);
  };

  const handleMarkAsFixed = async () => {
    if (!selectedEvent) return;
    
    try {
      const response: any = await apiService.patch(
        `/inventory-events/${selectedEvent._id}/fix`,
        { fixNotes }
      );
      
      if (response.success) {
        setFixDialogOpen(false);
        loadEvents();
        loadStatistics();
      }
    } catch (err) {
      console.error('خطأ في تحديد الحدث كمُصلح:', err);
      alert('فشل تحديد الحدث كمُصلح');
    }
  };

  const getStatusColor = (status: string): "success" | "error" => {
    return status === 'success' ? 'success' : 'error';
  };

  const getStatusIcon = (status: string) => {
    return status === 'success' ? <CheckCircleIcon /> : <ErrorIcon />;
  };

  if (loading && events.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  // التحقق من صلاحية الوصول للصفحة
  if (!canAccessPage) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert
          severity="error"
          icon={<LockIcon />}
          sx={{ mb: 2 }}
        >
          <Typography variant="h6" gutterBottom>
            ليس لديك صلاحية للوصول إلى هذه الصفحة
          </Typography>
          <Typography variant="body2">
            يرجى التواصل مع مدير النظام للحصول على الصلاحيات اللازمة.
          </Typography>
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h4" component="h1">
          أحداث معاملات المخزون
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          {canDeleteOld && (
            <Button
              variant="outlined"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={handleOpenDeleteDialog}
            >
              حذف السجلات القديمة
            </Button>
          )}
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={() => {
              loadEvents();
              loadStatistics();
              loadEventsCounts();
            }}
          >
            تحديث
          </Button>
        </Box>
      </Box>

      {/* Statistics Cards */}
      {statistics && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  إجمالي الأحداث
                </Typography>
                <Typography variant="h4">
                  {statistics.total}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  الأحداث الناجحة
                </Typography>
                <Typography variant="h4" color="success.main">
                  {statistics.success}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  الأحداث الفاشلة
                </Typography>
                <Typography variant="h4" color="error.main">
                  {statistics.failed}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  تحتاج إصلاح
                </Typography>
                <Typography variant="h4" color="warning.main">
                  {statistics.needingFix}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <FilterListIcon sx={{ mr: 1 }} />
            <Typography variant="h6">الفلاتر</Typography>
          </Box>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                label="بحث"
                placeholder="رقم الطلب، رقم المستند، المستودع..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                select
                fullWidth
                label="الحالة"
                value={filters.status}
                onChange={(e) => handleFilterChange('status', e.target.value)}
                size="small"
              >
                <MenuItem value="">الكل</MenuItem>
                <MenuItem value="success">ناجح</MenuItem>
                <MenuItem value="failed">فاشل</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                select
                fullWidth
                label="نوع الحدث"
                value={filters.eventType}
                onChange={(e) => handleFilterChange('eventType', e.target.value)}
                size="small"
              >
                <MenuItem value="">الكل</MenuItem>
                <MenuItem value="step_completed">خطوة مكتملة</MenuItem>
                <MenuItem value="transaction_completed">معاملة مكتملة</MenuItem>
                <MenuItem value="transaction_failed">معاملة فاشلة</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6} md={1.5}>
              <TextField
                fullWidth
                type="date"
                label="من تاريخ"
                value={filters.startDate}
                onChange={(e) => handleFilterChange('startDate', e.target.value)}
                size="small"
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={1.5}>
              <TextField
                fullWidth
                type="date"
                label="إلى تاريخ"
                value={filters.endDate}
                onChange={(e) => handleFilterChange('endDate', e.target.value)}
                size="small"
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={1.5}>
              <Button
                fullWidth
                variant={filters.needsFixOnly ? 'contained' : 'outlined'}
                color="warning"
                onClick={() => handleFilterChange('needsFixOnly', !filters.needsFixOnly)}
                size="small"
                sx={{ height: '40px' }}
              >
                تحتاج إصلاح فقط
              </Button>
            </Grid>
            <Grid item xs={12} sm={6} md={1.5}>
              <Button
                fullWidth
                variant="outlined"
                onClick={handleResetFilters}
                size="small"
                sx={{ height: '40px' }}
              >
                إعادة تعيين
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Events Table */}
      <Card>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2 }}>
            الأحداث ({totalEvents})
          </Typography>
          <TableContainer component={Paper} variant="outlined">
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell width="50px"></TableCell>
                  <TableCell>رقم الطلب</TableCell>
                  <TableCell>رقم المستند</TableCell>
                  <TableCell>نوع العملية</TableCell>
                  <TableCell>من مستودع</TableCell>
                  <TableCell>إلى مستودع</TableCell>
                  <TableCell>الحالة</TableCell>
                  <TableCell>الخطوات</TableCell>
                  <TableCell>آخر تحديث</TableCell>
                  <TableCell align="center">الإجراءات</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {groupedRequests.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={10} align="center">
                      <Typography color="textSecondary">
                        لا توجد أحداث
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  groupedRequests.map((group) => (
                    <React.Fragment key={group.requestId}>
                      <TableRow
                        hover
                        sx={{ cursor: 'pointer', '& > *': { borderBottom: expandedRows.has(group.requestId) ? 'none' : undefined } }}
                        onClick={() => toggleRow(group.requestId)}
                      >
                        <TableCell>
                          <IconButton size="small">
                            {expandedRows.has(group.requestId) ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                          </IconButton>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight="medium">
                            #{group.requestId.slice(-8)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="primary" fontWeight="medium">
                            {group.referenceNumber || '-'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={
                              group.transferType === 'direct' ? 'تحويل مباشر' :
                              group.transferType === 'request' ? 'طلب تحويل' :
                              (group.transferType === 'count_approval' || group.transferType === 'count_cancellation') ? 'اعتماد جرد' :
                              group.transferType === 'invoice' ? 'فاتورة' :
                              'غير محدد'
                            }
                            color={
                              group.transferType === 'direct' ? 'primary' :
                              group.transferType === 'request' ? 'secondary' :
                              (group.transferType === 'count_approval' || group.transferType === 'count_cancellation') ? 'success' :
                              group.transferType === 'invoice' ? 'info' :
                              'default'
                            }
                            size="small"
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>{group.fromWarehouse}</TableCell>
                        <TableCell>{group.toWarehouse}</TableCell>
                        <TableCell>
                          <Chip
                            icon={group.status === 'success' ? <CheckCircleIcon /> : <ErrorIcon />}
                            label={
                              group.status === 'success' ? 'مكتمل' :
                              group.status === 'failed' ? 'فاشل' : 'جزئي'
                            }
                            color={
                              group.status === 'success' ? 'success' :
                              group.status === 'failed' ? 'error' : 'warning'
                            }
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {group.completedSteps} / {group.totalSteps}
                            {group.failedSteps > 0 && (
                              <Chip
                                label={`${group.failedSteps} فاشل`}
                                size="small"
                                color="error"
                                sx={{ ml: 1 }}
                              />
                            )}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {format(new Date(group.lastUpdate), 'yyyy-MM-dd HH:mm', { locale: ar })}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          {canViewDetails && (
                            <Tooltip title="عرض التفاصيل">
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleViewDetails(group.events[0]);
                                }}
                              >
                                <VisibilityIcon />
                              </IconButton>
                            </Tooltip>
                          )}
                        </TableCell>
                      </TableRow>
                      {expandedRows.has(group.requestId) && (
                        <TableRow>
                          <TableCell colSpan={8} sx={{ py: 0, bgcolor: 'grey.50' }}>
                            <Box sx={{ p: 2 }}>
                              <Typography variant="subtitle2" gutterBottom>
                                تفاصيل الخطوات:
                              </Typography>
                              <Table size="small">
                                <TableHead>
                                  <TableRow>
                                    <TableCell>الحالة</TableCell>
                                    <TableCell>الخطوة</TableCell>
                                    <TableCell>التوقيت</TableCell>
                                    <TableCell>التفاصيل</TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {group.events.map((event) => (
                                    <TableRow key={event._id}>
                                      <TableCell>
                                        <Chip
                                          icon={getStatusIcon(event.status)}
                                          label={event.status === 'success' ? 'ناجح' : 'فاشل'}
                                          color={getStatusColor(event.status)}
                                          size="small"
                                        />
                                      </TableCell>
                                      <TableCell>{event.stepNameAr}</TableCell>
                                      <TableCell>
                                        {format(new Date(event.timestamp), 'HH:mm:ss', { locale: ar })}
                                      </TableCell>
                                      <TableCell>
                                        {event.error ? (
                                          <Box>
                                            <Typography variant="body2" color="error" sx={{ fontWeight: 'bold' }}>
                                              {event.error}
                                            </Typography>
                                            {event.details?.failedItems && event.details.failedItems.length > 0 && (
                                              <Box sx={{ mt: 1, pl: 2 }}>
                                                {event.details.failedItems.map((item: any, idx: number) => (
                                                  <Typography key={idx} variant="caption" color="error" display="block">
                                                    • {item.item}: {item.message}
                                                  </Typography>
                                                ))}
                                              </Box>
                                            )}
                                            {event.details?.errorMessage && (
                                              <Typography variant="caption" color="textSecondary" display="block" sx={{ mt: 0.5 }}>
                                                {event.details.errorMessage}
                                              </Typography>
                                            )}
                                          </Box>
                                        ) : event.details?.productName ? (
                                          <Typography variant="body2">
                                            {event.details.productName} - {event.details.quantity}
                                          </Typography>
                                        ) : (
                                          <Typography variant="body2" color="textSecondary">
                                            -
                                          </Typography>
                                        )}
                                      </TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            </Box>
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination */}
          {totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
              <Pagination
                count={totalPages}
                page={page}
                onChange={(e, value) => setPage(value)}
                color="primary"
              />
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Details Dialog */}
      <Dialog
        open={detailsDialogOpen}
        onClose={() => setDetailsDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>تفاصيل الحدث</DialogTitle>
        <DialogContent>
          {selectedEvent && (
            <Box sx={{ pt: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="textSecondary">
                    الحالة
                  </Typography>
                  <Chip
                    icon={getStatusIcon(selectedEvent.status)}
                    label={selectedEvent.status === 'success' ? 'ناجح' : 'فاشل'}
                    color={getStatusColor(selectedEvent.status)}
                    size="small"
                  />
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="textSecondary">
                    نوع الحدث
                  </Typography>
                  <Typography>{selectedEvent.eventTypeAr}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="textSecondary">
                    الخطوة
                  </Typography>
                  <Typography>{selectedEvent.stepNameAr}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="textSecondary">
                    التوقيت
                  </Typography>
                  <Typography>
                    {format(new Date(selectedEvent.timestamp), 'yyyy-MM-dd HH:mm:ss', { locale: ar })}
                  </Typography>
                </Grid>
                {selectedEvent.userId && (
                  <Grid item xs={6}>
                    <Typography variant="subtitle2" color="textSecondary">
                      المستخدم
                    </Typography>
                    <Typography>{selectedEvent.userId.name}</Typography>
                  </Grid>
                )}
                {selectedEvent.error && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="textSecondary">
                      رسالة الخطأ
                    </Typography>
                    <Alert severity="error" sx={{ mt: 1 }}>
                      {selectedEvent.error}
                    </Alert>
                  </Grid>
                )}
                {selectedEvent.details && Object.keys(selectedEvent.details).length > 0 && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="textSecondary" sx={{ mb: 1 }}>
                      التفاصيل
                    </Typography>
                    <Paper variant="outlined" sx={{ p: 2 }}>
                      <pre style={{ margin: 0, fontSize: '12px' }}>
                        {JSON.stringify(selectedEvent.details, null, 2)}
                      </pre>
                    </Paper>
                  </Grid>
                )}
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailsDialogOpen(false)}>
            إغلاق
          </Button>
        </DialogActions>
      </Dialog>

      {/* Fix Dialog */}
      <Dialog
        open={fixDialogOpen}
        onClose={() => setFixDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>تحديد الحدث كمُصلح</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            multiline
            rows={4}
            label="ملاحظات الإصلاح"
            value={fixNotes}
            onChange={(e) => setFixNotes(e.target.value)}
            sx={{ mt: 2 }}
            placeholder="اشرح كيف تم إصلاح المشكلة..."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFixDialogOpen(false)}>
            إلغاء
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleMarkAsFixed}
            disabled={!fixNotes.trim()}
          >
            تحديد كمُصلح
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Old Events Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => !deleting && setDeleteDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <WarningIcon color="warning" />
            حذف السجلات القديمة
          </Box>
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 3 }}>
            تحذير: هذه العملية لا يمكن التراجع عنها!
          </Alert>

          {eventsCounts && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                عدد السجلات الحالية:
              </Typography>
              <Typography variant="h6" gutterBottom>
                إجمالي: {eventsCounts.total} سجل
              </Typography>
              <Typography variant="body2" color="text.secondary">
                • أقدم من 3 شهور: {eventsCounts.olderThan3Months} سجل
              </Typography>
              <Typography variant="body2" color="text.secondary">
                • أقدم من 6 شهور: {eventsCounts.olderThan6Months} سجل
              </Typography>
            </Box>
          )}

          <TextField
            select
            fullWidth
            label="اختر الفترة"
            value={deletePeriod}
            onChange={(e) => setDeletePeriod(e.target.value as 'all' | '3months' | '6months')}
            disabled={deleting}
          >
            <MenuItem value="3months">
              حذف السجلات الأقدم من 3 شهور ({eventsCounts?.olderThan3Months || 0} سجل)
            </MenuItem>
            <MenuItem value="6months">
              حذف السجلات الأقدم من 6 شهور ({eventsCounts?.olderThan6Months || 0} سجل)
            </MenuItem>
            <MenuItem value="all">
              <Box sx={{ color: 'error.main' }}>
                حذف جميع السجلات ({eventsCounts?.total || 0} سجل)
              </Box>
            </MenuItem>
          </TextField>

          <Alert severity="info" sx={{ mt: 2 }}>
            {deletePeriod === 'all' && 'سيتم حذف جميع السجلات من قاعدة البيانات'}
            {deletePeriod === '3months' && 'سيتم حذف السجلات الأقدم من 3 شهور فقط'}
            {deletePeriod === '6months' && 'سيتم حذف السجلات الأقدم من 6 شهور فقط'}
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDeleteDialogOpen(false)}
            disabled={deleting}
          >
            إلغاء
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDeleteOldEvents}
            disabled={deleting}
            startIcon={deleting ? <CircularProgress size={20} /> : <DeleteIcon />}
          >
            {deleting ? 'جاري الحذف...' : 'تأكيد الحذف'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default InventoryEvents;